import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import PDFViewer from '../components/PDFViewer';
import { usePermission } from '../contexts/PermissionContext';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import { ContentsAPI, UploadsAPI, UploadGroupsAPI, S3API } from '../api/api';
import {
    ChevronRight,
    ChevronLeft,
    X,
    Download,
    Maximize2,
    Minimize2,
    FolderOpen,
    Image,
    BookOpen,
    GraduationCap,
    Video,
    FileText,
    Presentation,
    Users,
    Calendar,
    Clock,
    Award,
    Target,
    Lightbulb,
    Brain
} from 'lucide-react';
import { LUCIDE_ICON_MAP } from '../components/LucideIconPicker';
import './VisionPage.css';

// 제목 기반 아이콘 매핑 함수
const getIconByTitle = (title, size = 48) => {
    const lowerTitle = title?.toLowerCase() || '';

    if (lowerTitle.includes('신입') || lowerTitle.includes('입문') || lowerTitle.includes('기초')) {
        return <GraduationCap size={size} />;
    }
    if (lowerTitle.includes('영상') || lowerTitle.includes('동영상') || lowerTitle.includes('video')) {
        return <Video size={size} />;
    }
    if (lowerTitle.includes('세미나') || lowerTitle.includes('워크숍') || lowerTitle.includes('발표')) {
        return <Presentation size={size} />;
    }
    if (lowerTitle.includes('팀') || lowerTitle.includes('그룹') || lowerTitle.includes('협업')) {
        return <Users size={size} />;
    }
    if (lowerTitle.includes('일정') || lowerTitle.includes('스케줄') || lowerTitle.includes('월별')) {
        return <Calendar size={size} />;
    }
    if (lowerTitle.includes('시간') || lowerTitle.includes('기한')) {
        return <Clock size={size} />;
    }
    if (lowerTitle.includes('자격') || lowerTitle.includes('인증') || lowerTitle.includes('수료')) {
        return <Award size={size} />;
    }
    if (lowerTitle.includes('목표') || lowerTitle.includes('성과')) {
        return <Target size={size} />;
    }
    if (lowerTitle.includes('아이디어') || lowerTitle.includes('창의')) {
        return <Lightbulb size={size} />;
    }
    if (lowerTitle.includes('심화') || lowerTitle.includes('전문') || lowerTitle.includes('고급')) {
        return <Brain size={size} />;
    }
    if (lowerTitle.includes('자료') || lowerTitle.includes('문서') || lowerTitle.includes('매뉴얼')) {
        return <FileText size={size} />;
    }

    return <BookOpen size={size} />;
};

// DB 아이콘 우선, 폴백으로 제목 기반 매핑
const getItemIcon = (item, size = 48) => {
    const LucideIcon = LUCIDE_ICON_MAP[item.icon];
    if (LucideIcon) return <LucideIcon size={size} />;
    return getIconByTitle(item.title, size);
};

// 제목 기반 설명 생성 함수
const getDescriptionByTitle = (title) => {
    const lowerTitle = title?.toLowerCase() || '';

    if (lowerTitle.includes('신입') || lowerTitle.includes('입문')) {
        return '신입 직원을 위한 기초 교육 자료입니다';
    }
    if (lowerTitle.includes('영상') || lowerTitle.includes('동영상')) {
        return '교육 동영상 콘텐츠를 시청합니다';
    }
    if (lowerTitle.includes('세미나') || lowerTitle.includes('워크숍')) {
        return '전문 세미나 및 워크숍 자료입니다';
    }
    if (lowerTitle.includes('자격') || lowerTitle.includes('인증')) {
        return '자격 취득 및 인증 관련 교육입니다';
    }
    if (lowerTitle.includes('심화') || lowerTitle.includes('고급')) {
        return '심화 과정 및 전문 교육 자료입니다';
    }
    if (lowerTitle.includes('매뉴얼') || lowerTitle.includes('가이드')) {
        return '업무 매뉴얼 및 가이드 문서입니다';
    }
    if (lowerTitle.includes('경력')) {
        return '경력 직원을 위한 교육 자료입니다';
    }

    return '지속적인 성장을 위한 학습 자료입니다';
};

// 제목 기반 뱃지 생성 함수
const getBadgesByTitle = (title) => {
    const lowerTitle = title?.toLowerCase() || '';

    // MZ부트캠프
    if (lowerTitle.includes('mz부트캠프 홈페이지') || lowerTitle.includes('mz 부트캠프 홈페이지')) {
        return [{ label: 'Website', primary: true }, { label: 'MZ부트캠프' }, { label: '플랫폼' }];
    }
    if (lowerTitle.includes('mz 부트캠프 소개서') || lowerTitle.includes('mz부트캠프 소개서')) {
        return [{ label: 'Document', primary: true }, { label: 'MZ부트캠프' }, { label: '소개' }];
    }
    if (lowerTitle.includes('mz부트캠프 규정집') || lowerTitle.includes('mz 부트캠프 규정집')) {
        return [{ label: 'Guideline', primary: true }, { label: 'MZ부트캠프' }, { label: '규정' }];
    }

    // 오프라인교육 플랫폼
    if (lowerTitle.includes('메타파운더리')) {
        return [{ label: 'Platform', primary: true }, { label: '메타파운더리' }, { label: '오프라인' }];
    }
    if (lowerTitle.includes('ca step-up') || lowerTitle.includes('step-up')) {
        return [{ label: 'Project', primary: true }, { label: 'CA' }, { label: '역량강화' }];
    }
    if (lowerTitle.includes('월간 테마교육') || lowerTitle.includes('테마교육')) {
        return [{ label: 'Monthly', primary: true }, { label: '테마교육' }, { label: '정기' }];
    }

    // 온라인교육 플랫폼
    if (lowerTitle.includes('fp파트너즈') || lowerTitle.includes('fp 파트너즈')) {
        return [{ label: 'Platform', primary: true }, { label: 'FP파트너즈' }, { label: '온라인' }];
    }
    if (lowerTitle.includes('통의보감')) {
        return [{ label: 'Platform', primary: true }, { label: '통의보감' }, { label: '온라인' }];
    }
    if (lowerTitle.includes('보케어')) {
        return [{ label: 'Platform', primary: true }, { label: '보케어' }, { label: '온라인' }];
    }

    // 신입관리
    if (lowerTitle.includes('신입 데이터 관리') || lowerTitle.includes('데이터 관리')) {
        return [{ label: 'Beginner', primary: true }, { label: '신입' }, { label: '데이터관리' }];
    }
    if (lowerTitle.includes('신입급여') || lowerTitle.includes('신입 급여')) {
        return [{ label: 'Beginner', primary: true }, { label: '신입' }, { label: '급여' }];
    }
    if (lowerTitle.includes('신입 매출별') || lowerTitle.includes('매출별 유형')) {
        return [{ label: 'Beginner', primary: true }, { label: '신입' }, { label: '매출분석' }];
    }

    return [{ label: 'Education', primary: true }, { label: '교육' }, { label: '학습' }];
};

function EducationPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { canAccessSection, getCategoriesBySection, getPartsByCategory, isLoading: permissionLoading } = usePermission();
    const { info, error: showError } = useAlert();

    const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';

    const [sections, setSections] = useState([]);
    const [dataLoading, setDataLoading] = useState(true);
    const [dataError, setDataError] = useState(null);

    // 활성 카테고리 (단일 선택 필터)
    const [activeCategory, setActiveCategory] = useState(null);

    useEffect(() => {
        if (!permissionLoading && !isAdmin && !canAccessSection('교육')) {
            navigate('/main');
        }
    }, [permissionLoading, isAdmin, canAccessSection, navigate]);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentImages, setCurrentImages] = useState([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [modalTitle, setModalTitle] = useState('');
    const [mediaItems, setMediaItems] = useState([]);
    const [currentMediaType, setCurrentMediaType] = useState('image');
    const [isFullscreen, setIsFullscreen] = useState(false);

    const [modalView, setModalView] = useState('closed');
    const [selectedPart, setSelectedPart] = useState(null);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [uploadGroups, setUploadGroups] = useState([]);
    const [groupsLoading, setGroupsLoading] = useState(false);

    // 썸네일 상태
    const [thumbnailUrls, setThumbnailUrls] = useState({});

    // 섹션 진입 시 모든 썸네일을 일괄 로딩하는 함수
    const loadAllThumbnails = useCallback(async (allItems) => {
        if (allItems.length === 0) return;

        const S3_URL_PATTERN = /^https:\/\/.*\.s3\..*\.amazonaws\.com\/(.+)$/;
        const batchItems = [];
        const immediateUrls = {};

        const needsGroupLookup = allItems.filter(item => !item.thumbnailUrl);
        const groupResults = await Promise.allSettled(
            needsGroupLookup.map(async (item) => {
                const res = await UploadGroupsAPI.getByPartId(item.partId, { silent: true });
                const groups = res.data?.groups || res.groups || [];
                return { partId: item.partId, groups };
            })
        );

        for (const item of allItems) {
            if (item.thumbnailUrl) {
                const match = item.thumbnailUrl.match(S3_URL_PATTERN);
                if (match) {
                    batchItems.push({ id: String(item.partId), type: 'S3_KEY', value: decodeURIComponent(match[1]) });
                } else {
                    immediateUrls[item.partId] = item.thumbnailUrl;
                }
            }
        }

        for (const result of groupResults) {
            if (result.status !== 'fulfilled') continue;
            const { partId, groups } = result.value;
            if (groups.length === 0) { immediateUrls[partId] = null; continue; }

            const group = groups[0];
            if (group.thumbnailS3Key) {
                batchItems.push({ id: String(partId), type: 'S3_KEY', value: group.thumbnailS3Key });
            } else if (group.thumbnailFileType === 'VIDEO') {
                immediateUrls[partId] = 'VIDEO_ICON';
            } else if (group.thumbnailUploadId) {
                batchItems.push({ id: String(partId), type: 'UPLOAD_ID', value: String(group.thumbnailUploadId), urlType: 'VIEW' });
            } else {
                immediateUrls[partId] = null;
            }
        }

        if (Object.keys(immediateUrls).length > 0) {
            setThumbnailUrls(prev => ({ ...prev, ...immediateUrls }));
        }

        if (batchItems.length > 0) {
            try {
                const batchRes = await S3API.batchPresign(batchItems);
                const results = batchRes.data?.results || batchRes.results || {};
                const urlMap = {};
                for (const [partId, data] of Object.entries(results)) {
                    urlMap[partId] = data ? data.url : null;
                }
                setThumbnailUrls(prev => ({ ...prev, ...urlMap }));
            } catch (error) {
                console.error('Failed to batch load thumbnails:', error);
                const failedMap = {};
                batchItems.forEach(item => { failedMap[item.id] = null; });
                setThumbnailUrls(prev => ({ ...prev, ...failedMap }));
            }
        }
    }, []);

    // 카테고리 선택 (단일 선택)
    const selectCategory = (category) => {
        setActiveCategory(category);
    };

    // 전체보기
    const showAll = () => {
        setActiveCategory(null);
    };

    // DB에서 교육 데이터 로드
    useEffect(() => {
        const loadEducationData = async () => {
            try {
                setDataLoading(true);
                setDataError(null);

                const menuResponse = await ContentsAPI.getMenu({ silent: true });
                const menuData = menuResponse.data || menuResponse;

                const educationSection = menuData.sections?.find(s => s.name === '교육');
                if (!educationSection) {
                    setSections([]);
                    return;
                }

                const transformedSections = (educationSection.categories || []).map((category) => {
                    const items = (category.parts || []).map((part) => {
                        const hasGroups = part.partType === 'GALLERY' || part.partType === 'MONTHLY_TABS';

                        return {
                            id: part.id,
                            partId: part.id,
                            title: part.name,
                            icon: part.icon || '📄',
                            buttonText: part.buttonText || '바로가기',
                            url: part.partType === 'LINK' ? part.externalUrl : null,
                            hasGroups,
                            partType: part.partType,
                            categoryName: category.name,
                            thumbnailUrl: part.thumbnailUrl || null,
                        };
                    });

                    return {
                        id: category.id,
                        title: category.name,
                        icon: category.icon || '📚',
                        count: items.length,
                        items,
                    };
                });

                setSections(transformedSections);

                // 모든 썸네일 일괄 로딩 (silent - 로딩 스피너 미표시)
                const allParts = transformedSections.flatMap(section =>
                    section.items.map(item => ({
                        partId: item.partId,
                        thumbnailUrl: item.thumbnailUrl,
                    }))
                );
                loadAllThumbnails(allParts);
            } catch (error) {
                console.error('Failed to load education data:', error);
                setDataError(error.message || '데이터를 불러오는데 실패했습니다.');
            } finally {
                setDataLoading(false);
            }
        };

        if (!permissionLoading && (isAdmin || canAccessSection('교육'))) {
            loadEducationData();
        }
    }, [permissionLoading, isAdmin, canAccessSection]);

    // 권한에 따라 필터링된 섹션
    const filteredSections = useMemo(() => {
        if (isAdmin) {
            return sections;
        }

        const accessibleCategories = getCategoriesBySection('교육');
        const accessibleCategoryNames = accessibleCategories.map(c => c.name);

        return sections
            .filter(section => accessibleCategoryNames.includes(section.title))
            .map(section => {
                const accessibleParts = getPartsByCategory(section.title);
                const accessiblePartNames = accessibleParts.map(p => p.name);

                const filteredItems = section.items.filter(item =>
                    accessiblePartNames.includes(item.title)
                );

                return {
                    ...section,
                    items: filteredItems,
                    count: filteredItems.length
                };
            })
            .filter(section => section.items.length > 0);
    }, [isAdmin, sections, getCategoriesBySection, getPartsByCategory]);

    // 카테고리 목록
    const categories = useMemo(() => {
        return filteredSections.map(s => s.title);
    }, [filteredSections]);

    // 필터링된 섹션 (단일 카테고리 선택)
    const displayedSections = useMemo(() => {
        if (activeCategory === null) {
            return filteredSections;
        }
        return filteredSections.filter(section =>
            section.title === activeCategory
        );
    }, [filteredSections, activeCategory]);

    const handleCardClick = async (card) => {
        // URL이 있으면 외부 링크로 이동
        if (card.url) {
            window.open(card.url, '_blank', 'noopener,noreferrer');
            return;
        }

        // partId가 있으면 그룹 모달 시도 (hasGroups 여부와 관계없이)
        if (card.partId) {
            setSelectedPart(card);
            setModalTitle(card.title);
            setModalView('groups');
            document.body.style.overflow = 'hidden';
            await loadUploadGroups(card.partId);
            return;
        }

        info('이 항목에는 연결된 콘텐츠가 없습니다.');
    };

    const loadUploadGroups = async (partId) => {
        setGroupsLoading(true);
        try {
            const response = await UploadGroupsAPI.getByPartId(partId);
            setUploadGroups(response.data?.groups || response.groups || []);
        } catch (error) {
            console.error('Failed to load upload groups:', error);
            setUploadGroups([]);
        } finally {
            setGroupsLoading(false);
        }
    };

    const handleGroupSelect = async (group) => {
        setSelectedGroup(group);
        setGroupsLoading(true);
        try {
            const response = await UploadsAPI.getUploadsByGroupId(group.id, { limit: 1000 });
            const uploads = response.data?.uploads || response.uploads || [];

            // URL을 병렬로 요청
            const urlResults = await Promise.all(
                uploads.map(async (upload) => {
                    try {
                        const res = await S3API.getDownloadUrl(upload.id, 'VIEW');
                        return res.data?.url || res.url;
                    } catch (e) {
                        console.error('Failed to get presigned URL:', e);
                        return null;
                    }
                })
            );

            // 결과를 동기적으로 빌드 (순서 보장)
            const items = [];
            const urls = [];
            uploads.forEach((upload, index) => {
                const url = urlResults[index];
                if (url) {
                    const isPDF = upload.mimeType === 'application/pdf' ||
                                  upload.fileName?.toLowerCase().endsWith('.pdf');
                    const isVideo = upload.fileType === 'VIDEO' || upload.mimeType?.startsWith('video/');
                    items.push({
                        type: isPDF ? 'pdf' : isVideo ? 'video' : 'image',
                        src: url,
                        fileName: upload.originalName || upload.fileName
                    });
                    urls.push(url);
                }
            });

            setMediaItems(items);
            setCurrentImages(urls);
            setCurrentImageIndex(0);
            setCurrentMediaType(items[0]?.type || 'image');
            setModalView('files');
            setIsModalOpen(true);
        } catch (err) {
            console.error('Failed to load group files:', err);
            showError('파일을 불러오는데 실패했습니다.');
        } finally {
            setGroupsLoading(false);
        }
    };

    const handleBackToGroups = () => {
        setSelectedGroup(null);
        setCurrentImages([]);
        setMediaItems([]);
        setCurrentImageIndex(0);
        setModalView('groups');
        setIsModalOpen(false);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setCurrentImages([]);
        setMediaItems([]);
        setCurrentImageIndex(0);
        setModalTitle('');
        setCurrentMediaType('image');
        setIsFullscreen(false);
        setModalView('closed');
        setSelectedPart(null);
        setSelectedGroup(null);
        setUploadGroups([]);
        document.body.style.overflow = 'auto';
    };

    const nextImage = () => {
        const newIndex = (currentImageIndex + 1) % currentImages.length;
        setCurrentImageIndex(newIndex);
        if (mediaItems.length > 0) {
            setCurrentMediaType(mediaItems[newIndex].type);
        }
    };

    const prevImage = () => {
        const newIndex = (currentImageIndex - 1 + currentImages.length) % currentImages.length;
        setCurrentImageIndex(newIndex);
        if (mediaItems.length > 0) {
            setCurrentMediaType(mediaItems[newIndex].type);
        }
    };

    const goToMedia = (index) => {
        setCurrentImageIndex(index);
        if (mediaItems.length > 0) {
            setCurrentMediaType(mediaItems[index].type);
        }
    };

    const downloadCurrentImage = () => {
        const imageUrl = currentImages[currentImageIndex];
        const fileName = `${modalTitle}_${currentImageIndex + 1}.png`;
        fetch(imageUrl)
            .then((response) => response.blob())
            .then((blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            })
            .catch((err) => {
                console.error('다운로드 실패:', err);
                showError('다운로드에 실패했습니다.');
            });
    };

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') closeModal();
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, []);

    useEffect(() => {
        if (isModalOpen && currentImageIndex >= 0) {
            const thumbnailElement = document.querySelector(`.viewer-thumbnail:nth-child(${currentImageIndex + 1})`);
            if (thumbnailElement) {
                thumbnailElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'center',
                });
            }
        }
    }, [currentImageIndex, isModalOpen]);

    const isLoading = permissionLoading || dataLoading;

    if (isLoading) {
        return (
            <div className="vision-page">
                <Navigation />
                <div className="vision-loading">
                    <div className="loading-spinner"></div>
                    <p>로딩 중...</p>
                </div>
            </div>
        );
    }

    if (dataError) {
        return (
            <div className="vision-page">
                <Navigation />
                <div className="vision-error">
                    <p className="error-title">오류가 발생했습니다</p>
                    <p className="error-message">{dataError}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="vision-page">
            <Navigation />

            <div className="vision-container">
                {/* 페이지 헤더 */}
                <header className="vision-header">
                    <h1 className="vision-title">교육 <span className="vision-title-en">Education</span></h1>
                    <p className="vision-description">
                        지속적인 성장을 위한 학습 자료를 확인하세요
                    </p>
                </header>

                <div className="vision-layout">
                    {/* 메인 콘텐츠 영역 */}
                    <main className="vision-main">
                        {displayedSections.length === 0 ? (
                            <div className="vision-empty">
                                <p>접근 가능한 항목이 없습니다.</p>
                                <p className="empty-hint">관리자에게 권한을 요청해주세요.</p>
                            </div>
                        ) : (
                            <div className="category-list">
                                {displayedSections.map((section) => (
                                    <div key={section.id} className="category-section">
                                        <div className="category-header">
                                            <h2 className="category-title">{section.title}</h2>
                                            <span className="category-count">{section.items.length}</span>
                                        </div>

                                        <div className="category-items">
                                            {section.items.map((item) => (
                                                <article
                                                    key={item.id}
                                                    className="article-card"
                                                    onClick={() => handleCardClick(item)}
                                                >
                                                    <div className="article-content">
                                                        <div className="article-badges">
                                                            {getBadgesByTitle(item.title).map((badge, idx) => (
                                                                <span
                                                                    key={idx}
                                                                    className={`article-badge ${badge.primary ? '' : 'secondary'}`}
                                                                >
                                                                    {badge.label}
                                                                </span>
                                                            ))}
                                                        </div>
                                                        <h3 className="article-title">{item.title}</h3>
                                                        <p className="article-description">{getDescriptionByTitle(item.title)}</p>
                                                    </div>
                                                    <div className="article-thumbnail">
                                                        {thumbnailUrls[item.partId] === undefined ? (
                                                            <div className="thumbnail-skeleton"></div>
                                                        ) : thumbnailUrls[item.partId] === 'VIDEO_ICON' ? (
                                                            <div className="thumbnail-fallback video-thumbnail">
                                                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                                    <polygon points="5 3 19 12 5 21 5 3" fill="currentColor" />
                                                                </svg>
                                                            </div>
                                                        ) : thumbnailUrls[item.partId] ? (
                                                            <>
                                                                <img
                                                                    src={thumbnailUrls[item.partId]}
                                                                    alt={item.title}
                                                                    className="thumbnail-image"
                                                                    onError={(e) => {
                                                                        e.target.style.display = 'none';
                                                                        if (e.target.nextSibling) {
                                                                            e.target.nextSibling.style.display = 'flex';
                                                                        }
                                                                    }}
                                                                />
                                                                <div className="thumbnail-fallback" style={{ display: 'none' }}>
                                                                    {getItemIcon(item, 36)}
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <div className="thumbnail-fallback">
                                                                {getItemIcon(item, 36)}
                                                            </div>
                                                        )}
                                                    </div>
                                                </article>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </main>

                    {/* 사이드바 - 카테고리 필터 */}
                    {categories.length > 0 && (
                        <aside className="vision-sidebar">
                            <div className="sidebar-card">
                                <div className="sidebar-header">
                                    <span className="sidebar-title">카테고리</span>
                                </div>
                                <div className="sidebar-nav">
                                    <button
                                        className={`nav-item ${activeCategory === null ? 'active' : ''}`}
                                        onClick={showAll}
                                    >
                                        전체보기
                                    </button>
                                    {categories.map((category) => (
                                        <button
                                            key={category}
                                            className={`nav-item ${activeCategory === category ? 'active' : ''}`}
                                            onClick={() => selectCategory(category)}
                                        >
                                            {category}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </aside>
                    )}
                </div>
            </div>

            {/* 그룹 선택 모달 */}
            {modalView === 'groups' && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="group-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="group-modal-header">
                            <div className="group-modal-title-section">
                                <h2 className="group-modal-title">{modalTitle}</h2>
                                <span className="group-modal-count">{uploadGroups.length}개 자료</span>
                            </div>
                            <button className="modal-close-btn" onClick={closeModal}>
                                <X size={20} />
                            </button>
                        </div>

                        <div className="group-modal-body">
                            {groupsLoading ? (
                                <div className="groups-loading">
                                    <div className="loading-spinner small"></div>
                                    <span>자료 목록을 불러오는 중...</span>
                                </div>
                            ) : uploadGroups.length === 0 ? (
                                <div className="groups-empty">
                                    <FolderOpen size={48} strokeWidth={1} />
                                    <span>등록된 자료가 없습니다.</span>
                                </div>
                            ) : (
                                <div className="groups-list">
                                    {uploadGroups.map((group) => (
                                        <div
                                            key={group.id}
                                            className="group-item"
                                            onClick={() => handleGroupSelect(group)}
                                        >
                                            <div className="group-item-icon">
                                                <FolderOpen size={24} />
                                            </div>
                                            <div className="group-item-info">
                                                <span className="group-item-title">{group.title}</span>
                                                <span className="group-item-count">{group.fileCount || 0}개 파일</span>
                                            </div>
                                            <ChevronRight size={20} className="group-item-arrow" />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* 이미지/PDF 뷰어 모달 */}
            {isModalOpen && modalView === 'files' && (
                <div className="modal-overlay" onClick={closeModal}>
                    <button
                        className="viewer-nav-btn prev"
                        onClick={(e) => { e.stopPropagation(); prevImage(); }}
                        disabled={currentImages.length <= 1}
                    >
                        <ChevronLeft size={24} />
                    </button>

                    <div
                        className={`viewer-modal ${isFullscreen ? 'fullscreen' : ''} ${currentMediaType === 'pdf' && mediaItems.length <= 1 ? 'pdf-layout' : ''}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="viewer-header">
                            <div className="viewer-header-left">
                                <button className="viewer-back-btn" onClick={handleBackToGroups}>
                                    <ChevronLeft size={20} />
                                    <span>목록</span>
                                </button>
                                <h2 className="viewer-title">{selectedGroup?.title || modalTitle}</h2>
                            </div>
                            <div className="viewer-header-right">
                                <span className="viewer-counter">
                                    {currentImageIndex + 1} / {currentImages.length}
                                </span>
                                {currentMediaType !== 'pdf' && (
                                    <>
                                        <button className="viewer-action-btn" onClick={downloadCurrentImage} title="다운로드">
                                            <Download size={20} />
                                        </button>
                                        <button
                                            className="viewer-action-btn"
                                            onClick={() => setIsFullscreen(!isFullscreen)}
                                            title={isFullscreen ? '축소' : '전체화면'}
                                        >
                                            {isFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
                                        </button>
                                    </>
                                )}
                                <button
                                    className="viewer-close-btn"
                                    onClick={isFullscreen ? () => setIsFullscreen(false) : closeModal}
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        <div className="viewer-body">
                            {isFullscreen && (
                                <button
                                    className="fullscreen-close-btn"
                                    onClick={() => setIsFullscreen(false)}
                                    title="축소"
                                >
                                    <X size={28} />
                                </button>
                            )}

                            <div className="viewer-image-container">
                                {currentImages.length === 0 ? (
                                    <div className="no-images">
                                        <Image size={48} strokeWidth={1} />
                                        <span>파일이 없습니다</span>
                                    </div>
                                ) : currentMediaType === 'pdf' ? (
                                    <PDFViewer
                                        fileUrl={currentImages[currentImageIndex]}
                                        fileName={mediaItems[currentImageIndex]?.fileName}
                                    />
                                ) : currentMediaType === 'video' ? (
                                    <video
                                        key={currentImages[currentImageIndex]}
                                        src={currentImages[currentImageIndex]}
                                        controls
                                        autoPlay
                                        className="viewer-video"
                                    >
                                        Your browser does not support the video tag.
                                    </video>
                                ) : (
                                    <img
                                        src={currentImages[currentImageIndex]}
                                        alt={`${modalTitle} - ${currentImageIndex + 1}`}
                                        className="viewer-image"
                                    />
                                )}
                            </div>

                        </div>

                        <div className="viewer-thumbnails">
                            {currentImages.map((src, index) => {
                                const mediaType = mediaItems[index]?.type;
                                return (
                                    <div
                                        key={index}
                                        className={`viewer-thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                                        onClick={() => goToMedia(index)}
                                    >
                                        {mediaType === 'pdf' ? (
                                            <FileText size={18} />
                                        ) : mediaType === 'video' ? (
                                            <Video size={18} />
                                        ) : (
                                            <Image size={18} />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <button
                        className="viewer-nav-btn next"
                        onClick={(e) => { e.stopPropagation(); nextImage(); }}
                        disabled={currentImages.length <= 1}
                    >
                        <ChevronRight size={24} />
                    </button>
                </div>
            )}

            {/* 페이지 푸터 */}
            <footer className="vision-footer">
                <div className="footer-disclaimer">
                    <strong>본 CIS 자료는 MAP Group의 지식재산</strong>이며, 무단 복제·배포 시<br />
                    <span className="footer-highlight">비밀유지서약에 따른 법적 책임</span>을 부담해야 합니다.
                </div>
            </footer>
        </div>
    );
}

export default EducationPage;
